import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Button from 'devextreme-react/button';
import LoadIndicator from 'devextreme-react/load-indicator';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { custom } from 'devextreme/ui/dialog';
import { displayNameFromPath } from '../mediaViewer/mediaFileTypes';
import {
  fileAccessRestrictedHref,
  isFileAccessRestrictedStatus
} from '../storageAccess/fileAccessRestrictedUrls';
import { useShellToolbarCenter } from '../shell/ShellToolbarContext';
import { showErrorToast, showSuccessToast, showWarningToast } from '../notifications/toast';
import {
  ApiHttpError,
  ContentRevisionConflictError,
  copyPathForConflict,
  fetchContent,
  saveContent,
  type StorageContentResponse
} from '../textEditor/textEditorApi';
import {
  isDrawioSameOriginMessage,
  parseDrawioMessage,
  postDrawioExport,
  postDrawioLoad
} from './drawioEmbedProtocol';
import { buildDrawioEmbedSrc } from './drawioTypes';
import { getEditorAutosaveIntervalMs } from '../editorShell/editorAutosavePreference';
import {
  isEmbeddedEditor,
  resolveEditorChromeMode,
  type EditorChromeMode
} from '../editorShell/editorChrome';
import { MinimalEditorChrome } from '../editorShell/MinimalEditorChrome';
import './drawioEditor.css';

const DRAWIO_CONTENT_TYPE = 'application/xml';

export interface DrawioEditorPageProps {
  filesLinkTo?: string;
  filesLinkLabel?: string;
  chrome?: EditorChromeMode;
}

type LoadState = 'idle' | 'loading' | 'loaded' | 'error';

async function showRevisionConflictDialog(): Promise<'reload' | 'copy' | 'cancel'> {
  const dialog = custom({
    title: 'Revision conflict',
    messageHtml:
      '<p style="margin:0;line-height:1.45">The file changed on the server since you opened it. Reload the latest version or save your edits as a copy.</p>',
    buttons: [
      {
        text: 'Reload',
        type: 'default',
        stylingMode: 'contained',
        onClick: () => 'reload' as const
      },
      {
        text: 'Save as copy',
        stylingMode: 'outlined',
        onClick: () => 'copy' as const
      },
      {
        text: 'Cancel',
        stylingMode: 'text',
        onClick: () => 'cancel' as const
      }
    ]
  });

  try {
    const result = await dialog.show();
    if (result === 'reload' || result === 'copy' || result === 'cancel') return result;
    return 'cancel';
  } catch {
    return 'cancel';
  }
}

export function DrawioEditorPage({
  filesLinkTo = '/files',
  filesLinkLabel = 'File Browser',
  chrome: chromeProp
}: DrawioEditorPageProps) {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const mountId = (params.get('mountId') ?? '').trim();
  const path = (params.get('path') ?? '').trim();
  const viewOnly = (params.get('mode') ?? '').trim().toLowerCase() === 'view';
  const hasSource = Boolean(mountId && path);
  const embedded = isEmbeddedEditor(params);
  const chrome = resolveEditorChromeMode(params, chromeProp);

  useShellToolbarCenter(null);

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const editorReadyRef = useRef(false);
  const pendingXmlRef = useRef<string | null>(null);
  const exportResolverRef = useRef<((xml: string) => void) | null>(null);
  const savedXmlRef = useRef('');

  const [loadState, setLoadState] = useState<LoadState>(hasSource ? 'loading' : 'idle');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [revision, setRevision] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const embedSrc = useMemo(() => buildDrawioEmbedSrc('', viewOnly), [viewOnly]);
  const title = path ? displayNameFromPath(path) : '';

  useEffect(() => {
    const previousTitle = document.title;
    document.title = title ? `${title} — Draw.io Editor` : 'JARVIS Draw.io Editor';
    return () => {
      document.title = previousTitle;
    };
  }, [title]);

  const loadEditorContent = useCallback(
    (xml: string) => {
      pendingXmlRef.current = xml;
      if (editorReadyRef.current) {
        postDrawioLoad(iframeRef.current, xml, viewOnly ? 0 : 1);
        pendingXmlRef.current = null;
      }
    },
    [viewOnly]
  );

  const applyLoadedContent = useCallback((payload: StorageContentResponse) => {
    const xml = payload.content ?? '';
    savedXmlRef.current = xml;
    setRevision(payload.revision);
    setDirty(false);
    setLoadState('loaded');
    loadEditorContent(xml);
  }, [loadEditorContent]);

  useEffect(() => {
    if (!hasSource) {
      setLoadState('idle');
      return;
    }

    let cancelled = false;
    setLoadState('loading');
    setLoadError(null);
    editorReadyRef.current = false;
    pendingXmlRef.current = null;

    fetchContent(mountId, path)
      .then((payload) => {
        if (!cancelled) applyLoadedContent(payload);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        if (error instanceof ApiHttpError && isFileAccessRestrictedStatus(error.status)) {
          navigate(fileAccessRestrictedHref(), { replace: true });
          return;
        }
        const message = error instanceof Error ? error.message : 'Could not load diagram.';
        setLoadError(message);
        setLoadState('error');
      });

    return () => {
      cancelled = true;
    };
  }, [applyLoadedContent, hasSource, mountId, navigate, path]);

  const requestExportXml = useCallback((): Promise<string> => {
    return new Promise((resolve, reject) => {
      if (!editorReadyRef.current) {
        reject(new Error('Editor is not ready.'));
        return;
      }
      exportResolverRef.current = resolve;
      postDrawioExport(iframeRef.current, 'xml');
      window.setTimeout(() => {
        if (exportResolverRef.current === resolve) {
          exportResolverRef.current = null;
          reject(new Error('Timed out waiting for diagram export.'));
        }
      }, 15000);
    });
  }, []);

  const persist = useCallback(
    async (options?: { silent?: boolean; forceExport?: boolean }) => {
      if (viewOnly || !hasSource || loadState !== 'loaded') return false;
      if (!dirty && !options?.forceExport) return true;

      setSaving(true);
      try {
        const xml = await requestExportXml();
        const result = await saveContent(mountId, {
          path,
          content: xml,
          contentType: DRAWIO_CONTENT_TYPE,
          expectedRevision: revision ?? undefined
        });
        savedXmlRef.current = xml;
        setRevision(result.revision);
        setDirty(false);
        if (!options?.silent) showSuccessToast('Diagram saved.');
        return true;
      } catch (error) {
        if (error instanceof ContentRevisionConflictError) {
          const choice = await showRevisionConflictDialog();
          if (choice === 'reload') {
            const payload = await fetchContent(mountId, path);
            applyLoadedContent(payload);
            return false;
          }
          if (choice === 'copy') {
            const copyPath = copyPathForConflict(path);
            const xml = await requestExportXml();
            const result = await saveContent(mountId, {
              path: copyPath,
              content: xml,
              contentType: DRAWIO_CONTENT_TYPE
            });
            setParams({
              mountId,
              path: result.path
            });
            savedXmlRef.current = xml;
            setRevision(result.revision);
            setDirty(false);
            showSuccessToast('Saved as copy.');
            return true;
          }
          return false;
        }
        const message = error instanceof Error ? error.message : 'Could not save diagram.';
        if (!options?.silent) showErrorToast(message);
        else showWarningToast(message);
        return false;
      } finally {
        setSaving(false);
      }
    },
    [
      applyLoadedContent,
      dirty,
      hasSource,
      loadState,
      mountId,
      path,
      requestExportXml,
      revision,
      setParams,
      viewOnly
    ]
  );

  const onSave = useCallback(() => {
    void persist({ forceExport: true });
  }, [persist]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 's') {
        event.preventDefault();
        if (!viewOnly) void persist({ forceExport: true });
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [persist, viewOnly]);

  useEffect(() => {
    if (!dirty || loadState !== 'loaded') return;
    const autosaveMs = getEditorAutosaveIntervalMs();
    if (!autosaveMs) return;
    const timer = window.setInterval(() => {
      void persist({ silent: true });
    }, autosaveMs);
    return () => window.clearInterval(timer);
  }, [dirty, loadState, persist]);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (!isDrawioSameOriginMessage(event)) return;
      const message = parseDrawioMessage(event.data);
      if (!message) return;

      if (message.event === 'init') {
        editorReadyRef.current = true;
        const pending = pendingXmlRef.current ?? savedXmlRef.current;
        if (pending) {
          postDrawioLoad(iframeRef.current, pending, viewOnly ? 0 : 1);
          pendingXmlRef.current = null;
        }
        return;
      }

      if (!viewOnly && (message.event === 'autosave' || message.event === 'save')) {
        if (message.xml) {
          setDirty(message.xml !== savedXmlRef.current);
        }
        return;
      }

      if (message.event === 'export') {
        const xml = message.data ?? message.xml ?? '';
        if (exportResolverRef.current) {
          exportResolverRef.current(xml);
          exportResolverRef.current = null;
        }
      }
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [viewOnly]);

  if (!hasSource) {
    return (
      <div className="ops-fill-page ops-fill-page--bare drawio-editor-page">
        <div className="drawio-editor-page__empty">
          <p>
            Open a diagram from the <Link to={filesLinkTo}>{filesLinkLabel}</Link> to edit Draw.io
            Editor files.
          </p>
        </div>
      </div>
    );
  }

  const header = (
    <header className="drawio-editor-page__header" aria-label="Draw.io Editor toolbar">
      {chrome === 'full' ? (
        <Link to={filesLinkTo} className="dx-link">
          {filesLinkLabel}
        </Link>
      ) : null}
      <span className="drawio-editor-page__title" title={title}>
        {title}
      </span>
      <div className="drawio-editor-page__toolbar" role="toolbar" aria-label="Editor actions">
        {!viewOnly ? (
          <Button
            icon="save"
            text="Save"
            stylingMode="contained"
            type="default"
            disabled={loadState !== 'loaded' || saving}
            onClick={onSave}
          />
        ) : null}
      </div>
      <div className="drawio-editor-page__meta" aria-live="polite">
        <span className="drawio-editor-page__badge">Draw.io Editor</span>
        {viewOnly ? <span className="drawio-editor-page__badge">View only</span> : null}
        {dirty ? <span className="drawio-editor-page__dirty">Unsaved changes</span> : null}
        {saving ? <LoadIndicator height={18} width={18} visible /> : null}
        {loadState === 'error' && loadError ? (
          <span className="drawio-editor-page__badge drawio-editor-page__badge--error" role="alert">
            {loadError}
          </span>
        ) : null}
      </div>
    </header>
  );

  const stage = (
    <div className="drawio-editor-page__stage">
      {loadState === 'loading' ? (
        <div className="drawio-editor-page__empty" aria-busy="true">
          <LoadIndicator visible height={28} width={28} />
        </div>
      ) : null}
      {loadState === 'error' ? (
        <div className="drawio-editor-page__empty" role="alert">{loadError}</div>
      ) : null}
      {loadState === 'loaded' || loadState === 'loading' ? (
        <iframe
          ref={iframeRef}
          className="drawio-editor-page__iframe"
          title="Draw.io Editor"
          src={embedSrc}
          allow="clipboard-read; clipboard-write"
        />
      ) : null}
    </div>
  );

  const pageBody = (
    <>
      {header}
      {stage}
    </>
  );

  return (
    <div className="ops-fill-page ops-fill-page--bare drawio-editor-page">
      {chrome === 'minimal' && !embedded ? (
        <MinimalEditorChrome title={title || 'Draw.io Editor'}>{pageBody}</MinimalEditorChrome>
      ) : (
        pageBody
      )}
    </div>
  );
}
